import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchBannersCMS,
  editBanner,
  updateBanner,
  trashBanner,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";

const ListBanners = () => {
  const [banners, setBanners] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);

  const sourcePages = [
    "Home",
    "About Us",
    "Contact Us",
    "Specialities",
    "Academics",
    "Careers",
  ]; // Static dropdown options

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchBannersCMS();
        if (response?.data) {
          setBanners([...response.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        } else {
          toast.error("Failed to fetch banners.");
        }
      } catch (error) {
        toast.error("An error occurred while fetching banners.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editBanner(id);
      if (response?.data) {
        setEditItem(response.data);
      } else {
        toast.error("Failed to load banner data for editing.");
      }
    } catch (error) {
      toast.error("An error occurred while loading banner data.");
    }
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);

    const formData = new FormData();
    formData.append("name", editItem.name);
    formData.append("banner_heading", editItem.banner_heading);
    formData.append("banner_sub_heading", editItem.banner_sub_heading);
    formData.append("banner_content", editItem.banner_content);
    formData.append("source_page", editItem.source_page);

    if (editItem.img_path instanceof File) {
      formData.append("img_path", editItem.img_path);
    } else {
      formData.append("img_path", editItem.img_path);
    }

    // if (editItem.img_path instanceof File) {
    //   formData.append("img_path", editItem.img_path);
    // }
    try {
      const response = await updateBanner(editItem.id, formData);
      if (response && response.status === "true") {
        toast.success("Banner updated successfully!");
        setBanners((prev) =>
          prev.map((item) =>
            item.id === editItem.id ? { ...item, ...response.data } : item
          )
        );
        setEditItem(null);
      } else {
        toast.error("Failed to save changes.");
      }
    } catch (error) {
      toast.error("An error occurred while saving changes.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async (id) => {
    setLoadingAction(true);
    try {
      const response = await trashBanner(id);
      if (response && response.status === "true") {
        toast.success("Banner deleted successfully!");
        setBanners((prev) => prev.filter((item) => item.id !== id));
        setDeleteItemId(null);
      } else {
        toast.error("Failed to Deleted Banner");
      }
    } catch (error) {
      toast.error("Failed to delete banner.");
    } finally {
      setLoadingAction(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "name", headerName: "Name", width: 200 },
    { field: "banner_heading", headerName: "Banner Heading", width: 200 },
    { field: "source_page", headerName: "Source Page", width: 150 },
    {
      field: "img_path",
      headerName: "Image",
      width: 150,
      renderCell: (params) => {
        const imageUrl = params.row.img_path
          ? `${baseImage_path}/${params.row.img_path}`
          : "/default-banner.jpg"; // Use a default image if `img_path` is missing

        return (
          <img
            src={imageUrl}
            alt="Banner"
            style={{ width: "100%", height: "50px", objectFit: "cover" }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteItemId(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        List Banners
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid rows={banners} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open onClose={() => setEditItem(null)}>
          <DialogTitle>Edit Banner</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              value={editItem.name}
              onChange={(e) =>
                setEditItem({ ...editItem, name: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Banner Heading"
              value={editItem.banner_heading}
              onChange={(e) =>
                setEditItem({ ...editItem, banner_heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Banner Sub Heading"
              value={editItem.banner_sub_heading}
              onChange={(e) =>
                setEditItem({ ...editItem, banner_sub_heading: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Banner Content"
              multiline
              rows={3}
              value={editItem.banner_content}
              onChange={(e) =>
                setEditItem({ ...editItem, banner_content: e.target.value })
              }
            />

            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Image
              </Typography>

              <Button variant="contained" component="label">
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setEditItem({
                        ...editItem,
                        img_path: file,
                        img_preview: URL.createObjectURL(file), // temporary preview
                      });
                    }
                  }}
                />
              </Button>

              {/* Show image preview (either uploaded or from backend) */}
              {(editItem.img_preview || editItem.img_path) && (
                <Box mt={2}>
                  <Typography variant="body2">Image Preview:</Typography>
                  <img
                    src={
                      editItem.img_preview
                        ? editItem.img_preview
                        : `${baseImage_path}/${editItem.img_path}`
                    }
                    alt="Banner Preview"
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                </Box>
              )}
            </Box>
            {/* <Button variant="contained" component="label">
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  setEditItem({ ...editItem, img_path: e.target.files[0] })
                }
              />
            </Button> */}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditItem(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      {deleteItemId && (
        <Dialog open onClose={() => setDeleteItemId(null)}>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this banner?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteItemId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteItemId)}
              disabled={loadingAction}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ListBanners;
